import { useEffect, useRef, useCallback } from 'react';

interface BarcodeScannerOptions {
  onScan: (scannedValue: string) => void;
  isActive: boolean;
  minLength?: number;
  maxLength?: number;
  scanTimeout?: number;
  preventDefault?: boolean;
}

/**
 * Custom hook for handling barcode scanner input
 * Detects rapid sequential keystrokes typical of barcode scanners
 */
export const useBarcodeScanner = ({
  onScan,
  isActive,
  minLength = 3,
  maxLength = 50,
  scanTimeout = 100,
  preventDefault = true
}: BarcodeScannerOptions) => {
  const scanBuffer = useRef<string>('');
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastKeystroke = useRef<number>(0);

  const resetBuffer = useCallback(() => {
    scanBuffer.current = '';
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isActive) return;

    const now = Date.now();
    const timeSinceLastKeystroke = now - lastKeystroke.current;

    // If too much time has passed since last keystroke, reset buffer
    if (timeSinceLastKeystroke > scanTimeout && scanBuffer.current.length > 0) {
      resetBuffer();
    }

    lastKeystroke.current = now;

    // Handle Enter key (end of barcode scan)
    if (event.key === 'Enter') {
      const scannedValue = scanBuffer.current.trim();
      
      if (scannedValue.length >= minLength && scannedValue.length <= maxLength) {
        if (preventDefault) {
          event.preventDefault();
          event.stopPropagation();
        }
        
        console.log('🔍 Barcode scanned:', scannedValue);
        onScan(scannedValue);
      }
      
      resetBuffer();
      return;
    }

    // Handle regular characters
    if (event.key.length === 1) {
      // If this is the first character after a pause, start fresh
      if (timeSinceLastKeystroke > scanTimeout) {
        scanBuffer.current = '';
      }

      scanBuffer.current += event.key;

      // Clear buffer after timeout to prevent accumulation
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      
      scanTimeoutRef.current = setTimeout(() => {
        resetBuffer();
      }, scanTimeout * 10);

      // If we're building a scan, prevent default to avoid typing in input
      if (scanBuffer.current.length > 1 && preventDefault) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }, [isActive, minLength, maxLength, scanTimeout, preventDefault, onScan, resetBuffer]);

  useEffect(() => {
    if (isActive) {
      document.addEventListener('keydown', handleKeyDown, true);
      console.log('🔍 Barcode scanner activated');
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      resetBuffer();
      if (isActive) {
        console.log('🔍 Barcode scanner deactivated');
      }
    };
  }, [isActive, handleKeyDown, resetBuffer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetBuffer();
    };
  }, [resetBuffer]);

  return {
    resetBuffer
  };
};
